/**
 * Helpers UI — bandeja BNT de solicitudes de licencia (Comercial 2C).
 * Sin emisión automática, token ni endpoints SAT.
 */
import {
  LICENSE_REQUEST_STATUS_APPROVED,
  LICENSE_REQUEST_STATUS_IN_REVIEW,
  LICENSE_REQUEST_STATUS_ISSUED,
  LICENSE_REQUEST_STATUS_PENDING,
  LICENSE_REQUEST_STATUS_REJECTED,
} from "@/lib/commercialLicenseRequests";

export const PLATFORM_LICENSE_REQUEST_MSG_LIST_FORBIDDEN =
  "No tienes permiso para revisar solicitudes.";

export const PLATFORM_LICENSE_REQUEST_MSG_LIST_GENERIC =
  "No pudimos cargar las solicitudes. Intenta nuevamente.";

export const PLATFORM_LICENSE_REQUEST_MSG_NOT_FOUND =
  "No se encontró la solicitud.";

export const PLATFORM_LICENSE_REQUEST_MSG_PATCH_GENERIC =
  "No se pudo cambiar el estado de la solicitud.";

export const PLATFORM_LICENSE_REQUEST_MSG_PATCH_CLOSED =
  "La solicitud ya fue cerrada.";

export const PLATFORM_LICENSE_REQUEST_MSG_PATCH_SUCCESS =
  "Estado actualizado correctamente.";

export const PLATFORM_LICENSE_REQUEST_MSG_HEADER =
  "Revisa las solicitudes enviadas por compradores. Aprobar una solicitud no activa la licencia automáticamente; la emisión se realiza desde el Centro SaaS.";

export const PLATFORM_LICENSE_REQUEST_MSG_NO_AUTO_EMIT =
  "Aprobar no activa módulos ni genera token. Después de aprobar, emite o actualiza la licencia desde Centro SaaS.";

export const PLATFORM_LICENSE_REQUEST_MSG_APPROVED_NEXT =
  "Solicitud aprobada. Use «Emitir licencia web» para activar módulos en la cuenta del comprador (sin pegar token manual).";

export const PLATFORM_LICENSE_REQUEST_MSG_FULFILL_SUCCESS =
  "Licencia web emitida correctamente.";

export const PLATFORM_LICENSE_REQUEST_MSG_FULFILL_INCOMPLETE =
  "Emisión parcial: revise licencia y solicitud en Centro SaaS.";

export const PLATFORM_LICENSE_REQUEST_MSG_FULFILL_GENERIC =
  "No se pudo emitir la licencia web.";

export const PLATFORM_LICENSE_REQUEST_MSG_FULFILL_CLOSED =
  "La solicitud ya fue cerrada o emitida.";

export const PLATFORM_LICENSE_REQUEST_MSG_PAYMENT_PENDING =
  "Hay un comprobante de pago pendiente de validar.";

export const PLATFORM_LICENSE_REQUEST_MSG_PAYMENT_REJECTED =
  "El comprobante de pago fue rechazado.";

export const PLATFORM_LICENSE_REQUEST_REVIEW_PLACEHOLDER =
  "Notas de revisión (opcional)";

export const PLATFORM_LICENSE_REQUEST_EMIT_PATH = "/platform/licenses";

/** @param {string} status */
export function isPlatformLicenseRequestClosed(status) {
  const s = String(status || "").toLowerCase();
  return s === LICENSE_REQUEST_STATUS_REJECTED || s === LICENSE_REQUEST_STATUS_ISSUED;
}

/** @param {string} status */
export function platformLicenseRequestActions(status) {
  const s = String(status || "").toLowerCase();
  if (s === LICENSE_REQUEST_STATUS_PENDING) {
    return [
      { id: "in_review", label: "Marcar en revisión", status: LICENSE_REQUEST_STATUS_IN_REVIEW },
      { id: "approved", label: "Aprobar", status: LICENSE_REQUEST_STATUS_APPROVED },
      { id: "rejected", label: "Rechazar", status: LICENSE_REQUEST_STATUS_REJECTED },
    ];
  }
  if (s === LICENSE_REQUEST_STATUS_IN_REVIEW) {
    return [
      { id: "approved", label: "Aprobar", status: LICENSE_REQUEST_STATUS_APPROVED },
      { id: "rejected", label: "Rechazar", status: LICENSE_REQUEST_STATUS_REJECTED },
    ];
  }
  return [];
}

/** @param {object} row */
export function isPaymentProofPending(row) {
  return String(row?.payment_validation_status || "").toLowerCase() === "pending";
}

/** @param {object} row */
export function isPaymentProofRejected(row) {
  return String(row?.payment_validation_status || "").toLowerCase() === "rejected";
}

/** @param {object} row */
export function canFulfillPlatformLicenseRequest(row) {
  if (String(row?.status || "").toLowerCase() !== LICENSE_REQUEST_STATUS_APPROVED) return false;
  if (row?.linked_organization_license_id) return false;
  if (isPaymentProofPending(row) || isPaymentProofRejected(row)) return false;
  return true;
}

/**
 * Solicitud ya emitida: el diseñador puede ampliar/renovar creando una nueva revisión
 * de licencia para la organización enlazada (sin nueva solicitud del comprador).
 * @param {object} row
 */
export function canExpandIssuedPlatformLicenseRequest(row) {
  if (String(row?.status || "").toLowerCase() !== LICENSE_REQUEST_STATUS_ISSUED) return false;
  // Requiere organization_id: la ampliación crea una nueva revisión vía POST /platform/licenses.
  return Boolean(String(row?.organization_id || "").trim());
}

/** @param {object} row */
export function resolveIssuedRequestOrganizationId(row) {
  return String(row?.organization_id || "").trim() || null;
}

/** @param {object} result */
export function isFulfillResultComplete(result) {
  const lic = result?.license || {};
  const req = result?.request || {};
  return (
    Boolean(lic.id)
    && String(lic.status || "").toLowerCase() === "active"
    && String(req.status || "").toLowerCase() === LICENSE_REQUEST_STATUS_ISSUED
    && Boolean(req.linked_organization_license_id)
  );
}

export function parsePlatformLicenseRequestListError(err) {
  const status = err?.response?.status;
  if (status === 403) return PLATFORM_LICENSE_REQUEST_MSG_LIST_FORBIDDEN;
  return PLATFORM_LICENSE_REQUEST_MSG_LIST_GENERIC;
}

export function parsePlatformLicenseRequestPatchError(err) {
  const status = err?.response?.status;
  const text = typeof err?.response?.data?.detail === "string"
    ? err.response.data.detail
    : "";
  if (status === 403) return PLATFORM_LICENSE_REQUEST_MSG_LIST_FORBIDDEN;
  if (status === 404) return PLATFORM_LICENSE_REQUEST_MSG_NOT_FOUND;
  if (status === 400 && /cerrada/i.test(text)) return PLATFORM_LICENSE_REQUEST_MSG_PATCH_CLOSED;
  if (status === 400) return PLATFORM_LICENSE_REQUEST_MSG_PATCH_GENERIC;
  return PLATFORM_LICENSE_REQUEST_MSG_PATCH_GENERIC;
}

export function parsePlatformLicenseRequestFulfillError(err) {
  const status = err?.response?.status;
  const detail = err?.response?.data?.detail;
  const code = typeof detail === "object" ? detail?.code : null;
  const text = typeof detail === "string" ? detail : detail?.message || "";
  if (code === "PAYMENT_PROOF_PENDING") return PLATFORM_LICENSE_REQUEST_MSG_PAYMENT_PENDING;
  if (code === "PAYMENT_PROOF_REJECTED") return PLATFORM_LICENSE_REQUEST_MSG_PAYMENT_REJECTED;
  if (/pendiente/i.test(String(text))) return PLATFORM_LICENSE_REQUEST_MSG_PAYMENT_PENDING;
  if (/rechazado/i.test(String(text))) {
    return PLATFORM_LICENSE_REQUEST_MSG_PAYMENT_REJECTED;
  }
  if (/ya tiene|cerrada|aprobada/i.test(String(text))) {
    return PLATFORM_LICENSE_REQUEST_MSG_FULFILL_CLOSED;
  }
  return text || PLATFORM_LICENSE_REQUEST_MSG_FULFILL_GENERIC;
}

/** @param {string} [statusFilter] */
export function platformLicenseRequestStatusQuery(statusFilter) {
  const v = String(statusFilter || "").trim().toLowerCase();
  if (!v || v === "all" || v === "todas") return undefined;
  return v;
}

function _originalFromRow(row) {
  return row?.request_original || {};
}

function _currentFromRow(row) {
  return row?.current_commercial || {};
}

/** Nombre mostrado en la solicitud original (histórico, license_requests). */
export function licenseRequestOriginalDisplayName(row) {
  const snap = _originalFromRow(row);
  const rs = (snap.display_name || snap.razon_social || row?.razon_social || "").trim();
  const nc = (snap.nombre_comercial || row?.nombre_comercial || "").trim();
  if (rs && nc && rs !== nc && !snap.display_name) return `${rs} (${nc})`;
  return rs || nc || "—";
}

export function licenseRequestOriginalRfc(row) {
  const snap = _originalFromRow(row);
  return (snap.rfc || row?.rfc || "—").trim() || "—";
}

export function licenseRequestOriginalSummary(row) {
  const name = licenseRequestOriginalDisplayName(row);
  const rfc = licenseRequestOriginalRfc(row);
  if (name === "—" && rfc === "—") return "—";
  return `${name} · ${rfc}`;
}

/** Metadata comercial viva (organizations / distributor_buyers / system_config). */
export function licenseRequestCurrentCommercialName(row) {
  const cur = _currentFromRow(row);
  const live = (cur.name || row?.current_organization_name || "").trim();
  if (live) return live;
  return licenseRequestOriginalDisplayName(row);
}

export function licenseRequestCurrentCommercialRfc(row) {
  const cur = _currentFromRow(row);
  const live = (cur.rfc || row?.current_organization_rfc || "").trim();
  if (live) return live;
  return licenseRequestOriginalRfc(row);
}

/** Código comercial legible (buyer_code / org_code) — ID-first C4B. */
export function licenseRequestCommercialCodeLabel(row) {
  const cur = _currentFromRow(row);
  const buyerCode = (cur.buyer_code || row?.buyer_code || "").trim();
  const orgCode = (cur.org_code || row?.org_code || "").trim();
  if (buyerCode && orgCode && buyerCode !== orgCode) {
    return `${buyerCode} · ${orgCode}`;
  }
  return buyerCode || orgCode || "";
}

export function licenseRequestCommercialCodeSummary(row) {
  const code = licenseRequestCommercialCodeLabel(row);
  const rfc = licenseRequestCurrentCommercialRfc(row);
  if (code && rfc && rfc !== "—") return `${code} · RFC ${rfc}`;
  if (code) return code;
  return rfc !== "—" ? `RFC ${rfc}` : "";
}

export function licenseRequestCurrentCommercialSummary(row) {
  const name = licenseRequestCurrentCommercialName(row);
  const rfc = licenseRequestCurrentCommercialRfc(row);
  return `${name} · ${rfc}`;
}

export function licenseRequestHasCurrentCommercial(row) {
  const cur = _currentFromRow(row);
  return Boolean((cur.name || "").trim() || (cur.rfc || "").trim() || cur.source);
}

export function licenseRequestHasCommercialDrift(row) {
  if (row?.commercial_metadata_drift === true) return true;
  if (!licenseRequestHasCurrentCommercial(row)) return false;
  const on = licenseRequestOriginalDisplayName(row).toLowerCase();
  const cn = licenseRequestCurrentCommercialName(row).toLowerCase();
  const orfc = licenseRequestOriginalRfc(row).toUpperCase();
  const crfc = licenseRequestCurrentCommercialRfc(row).toUpperCase();
  return (cn && on && cn !== on) || (crfc && orfc && crfc !== orfc);
}

/** @deprecated use licenseRequestCurrentCommercialName */
export function licenseRequestDisplayName(row) {
  return licenseRequestCurrentCommercialName(row);
}

/** @deprecated use licenseRequestCurrentCommercialRfc */
export function licenseRequestDisplayRfc(row) {
  return licenseRequestCurrentCommercialRfc(row);
}

/** @deprecated use licenseRequestHasCommercialDrift */
export function licenseRequestHasLiveOrgOverride(row) {
  return licenseRequestHasCommercialDrift(row);
}

export function licenseRequestCurrentCommercialSourceLabel(row) {
  const src = (_currentFromRow(row).source || "").trim();
  if (src === "organizations") return "organizations";
  if (src === "distributor_buyers") return "distributor_buyers";
  if (src === "system_config") return "system_config license";
  return "—";
}
